/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useMemo, useState } from "react";
import { Alert, Dialog, DialogContent, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useQuery } from "@tanstack/react-query";
import api from "../../../api";
import { IndexTable } from "../../../components/index_table";
import { Loader } from "../../../components/loader";
import { OrderAppliedDiscountModal } from "../../../components/order_discount_modal";
import { OrderNumberCell } from "../../../components/order_number_cell";
import {
  CrossIconContainer,
  LoaderContainer,
  StyledDialogTitle,
} from "../../../components/audit_log_modal/styles";
import { Query_Keys } from "../../../lib/queryKeys";
import { CACHE_DURATION } from "../../../constants/cache_duration";
import camelize from "../../../utils/camelize";
import { formatDate } from "../../../utils/formatDate";
import { formatMoney } from "../../../utils/perqReportDrilldown";
import {
  DEFAULT_ORDERS_SORT_BY,
  DEFAULT_ORDERS_SORT_ORDER,
  getSubtotalOrdersApiParams,
} from "./constants";
import { StyledOrdersSummary } from "./styles";

const EMPTY_DURATION = "—";

// Room for the dialog's margins, title, summary, page-size row and pagination, so the table scrolls on its own.
const ORDERS_TABLE_MAX_HEIGHT = "max(200px, calc(100vh - 340px))";

const COLUMN_OPTIONS = [
  { key: "location", sortable: true },
  { key: "order_id", sortable: true },
  { key: "order_no", label: "Order #", sortable: true },
  { key: "order_details", label: "Order Details", sortable: false },
  { key: "opened", sortable: true },
  { key: "no_of_guest", label: "# of Guests", sortable: true },
  // The order's gross (amount + discounts), to the cent; summed over every page it matches the summary.
  { key: "subtotal_amount", label: "Gross", sortable: false },
  { key: "discount_amount", label: "All Discounts", sortable: true },
  { key: "amount", sortable: true },
  { key: "tax", sortable: true },
  { key: "tip", sortable: true },
  { key: "gratuity", sortable: true },
  { key: "total", sortable: true },
  { key: "paid", sortable: true },
  { key: "closed", sortable: true },
  { key: "duration", sortable: true },
  { key: "check_no", label: "Checks", sortable: true },
  { key: "tab_names", sortable: true },
];

const COLUMNS = COLUMN_OPTIONS.map((column) => ({
  key: column.key,
  label: column.label || camelize(column.key),
  sortable: column.sortable,
}));

const formatDuration = (seconds) => {
  if (seconds == null || seconds < 0) return EMPTY_DURATION;

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return [hrs, mins, secs].map((part) => part.toString().padStart(2, "0")).join(":");
};

/**
 * Lists the orders behind one Billing Report subtotal.
 * @param {{ isGroup: boolean, id: number, name: string, subtotalType: string, label: string }} selection
 * @param {object} apiFilters the report's applied filters, so the orders match the clicked number
 */
export const SubtotalOrdersModal = ({ selection, apiFilters, onClose }) => {
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState(DEFAULT_ORDERS_SORT_BY);
  const [sortOrder, setSortOrder] = useState(DEFAULT_ORDERS_SORT_ORDER);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const params = useMemo(
    () => getSubtotalOrdersApiParams(selection, apiFilters),
    [selection, apiFilters],
  );

  const fetchOrders = async () => {
    const response = await api.get("/v1/admin/transaction_fee_reports/orders", {
      params: { ...params, page, per_page: itemsPerPage, sort_by: sortBy, sort_order: sortOrder },
    });

    return {
      orders: response?.data?.orders || [],
      totalSubtotal: response?.data?.total_subtotal,
      pageCount: Number(response?.headers?.["total-count"] || 0),
    };
  };

  const { data, isFetching, error } = useQuery({
    queryKey: Query_Keys.billing_report_orders({ params, page, itemsPerPage, sortBy, sortOrder }),
    queryFn: fetchOrders,
    // Dropped once unused, so a reopened cell or page never opens with a total older than the report beside it.
    // staleTime still stops a reload behind the loader each time the tab regains focus.
    gcTime: 0,
    staleTime: CACHE_DURATION,
  });

  const fetchOrderDetail = async () => {
    const response = await api.get(`/v1/admin/orders/${selectedOrder?.order_id}/detail`);
    return response?.data;
  };

  const {
    data: orderDetail,
    isFetching: orderDetailFetching,
    isError: orderDetailError,
  } = useQuery({
    queryKey: Query_Keys.applied_discount({ selected_order: selectedOrder?.order_id }),
    queryFn: fetchOrderDetail,
    staleTime: CACHE_DURATION,
    enabled: Boolean(selectedOrder?.order_id),
  });

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setPage(1);
  };

  const handleOrderOpen = useCallback((order) => setSelectedOrder(order), []);

  const rows = useMemo(
    () =>
      (data?.orders || []).map((record) => ({
        id: record?.id,
        location: record?.location,
        order_id: record?.order_id,
        order_no: record?.order_no,
        order_details: <OrderNumberCell orderValue={record} onView={handleOrderOpen} />,
        opened: formatDate(record?.opened, record?.time_zone_abbr),
        no_of_guest: record?.no_of_guest,
        subtotal_amount: formatMoney(record?.subtotal_amount),
        discount_amount: formatMoney(record?.discount_amount),
        amount: formatMoney(record?.amount),
        tax: formatMoney(record?.tax),
        tip: formatMoney(record?.tip),
        gratuity: formatMoney(record?.gratuity),
        total: formatMoney(record?.total),
        paid: formatDate(record?.paid, record?.time_zone_abbr),
        closed: formatDate(record?.closed, record?.time_zone_abbr),
        duration: formatDuration(record?.duration),
        check_no: record?.check_no,
        tab_names: record?.tab_names,
      })),
    [data?.orders, handleOrderOpen],
  );

  const errorMessage = error ? error?.response?.data?.error || "Failed to load orders." : null;

  const renderContent = () => {
    if (isFetching) {
      return (
        <LoaderContainer data-testid="billing-report-orders-loader">
          <Loader />
        </LoaderContainer>
      );
    }

    if (errorMessage) {
      return (
        <Alert severity="error" data-testid="billing-report-orders-error">
          {errorMessage}
        </Alert>
      );
    }

    return (
      <IndexTable
        maxHeight={ORDERS_TABLE_MAX_HEIGHT}
        report={false}
        columns={COLUMNS}
        data={rows}
        type="orders"
        tableId="billing-report-orders"
        emptyStateText="No orders make up this subtotal"
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        page={page}
        setPage={setPage}
        pageCount={data?.pageCount || 0}
        sortMode="server"
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
      />
    );
  };

  return (
    <>
      <Dialog open onClose={onClose} fullWidth maxWidth="xl" data-testid="billing-report-orders-modal">
        <CrossIconContainer>
          <IconButton size="small" aria-label="Close" onClick={onClose} data-testid="billing-report-orders-modal-close">
            <ClearIcon />
          </IconButton>
        </CrossIconContainer>
        <StyledDialogTitle data-testid="billing-report-orders-modal-title">
          {`${selection.label} – ${selection.name}`}
        </StyledDialogTitle>
        {!isFetching && !errorMessage && data ? (
          <StyledOrdersSummary data-testid="billing-report-orders-summary">
            {`${selection.label}: ${formatMoney(data.totalSubtotal)}`}
          </StyledOrdersSummary>
        ) : null}
        <DialogContent>{renderContent()}</DialogContent>
      </Dialog>

      <OrderAppliedDiscountModal
        open={Boolean(selectedOrder)}
        handleClose={() => setSelectedOrder(null)}
        ordersData={orderDetail}
        isLoading={orderDetailFetching}
        isError={orderDetailError}
      />
    </>
  );
};
